import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useHeatmapEvents, useFeatureFlag, bucketEvents } from "@/lib/analytics";
import { HeatmapLayer } from "@/components/HeatmapLayer";
import { Locked } from "./analytics.index";
import { AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/analytics/incidents")({
  component: IncidentAnalyticsPage,
});

function IncidentAnalyticsPage() {
  const enabled = useFeatureFlag("advanced_analytics");
  const since = useMemo(() => new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString(), []);
  const { data: events = [] } = useHeatmapEvents({
    sources: ["incident", "geofence"],
    fromDate: since,
    limit: 5000,
  });

  if (!enabled.isLoading && !enabled.data) return <Locked />;

  const points = events.map((e) => ({
    lat: Number(e.latitude), lng: Number(e.longitude), weight: Number(e.weight) || 1,
  }));

  const bySev = events.reduce<Record<string, number>>((a, e) => { a[e.severity] = (a[e.severity] ?? 0) + 1; return a; }, {});
  const buckets = bucketEvents(events, 3);
  const repeat = buckets.filter((b) => b.incident_count >= 2).sort((a, b) => b.incident_count - a.incident_count).slice(0, 8);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Critical" value={bySev.critical ?? 0} className="text-destructive" />
        <Stat label="High" value={bySev.high ?? 0} className="text-warning" />
        <Stat label="Warning" value={bySev.warning ?? 0} className="text-warning" />
        <Stat label="Info" value={bySev.info ?? 0} className="text-muted-foreground" />
      </div>

      <HeatmapLayer points={points} height={460} radius={26} intensity={1.4} />

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-display text-lg font-semibold">
          <AlertTriangle className="mr-2 inline h-4 w-4 text-destructive" /> Repeat incident zones (30d)
        </h3>
        {repeat.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No repeat incident zones detected.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {repeat.map((r) => (
              <li key={`${r.bucket_lat}-${r.bucket_lng}`} className="flex items-center justify-between py-2 text-sm">
                <span className="font-mono text-xs text-muted-foreground">{r.bucket_lat.toFixed(3)}, {r.bucket_lng.toFixed(3)}</span>
                <span><b className="text-destructive">{r.incident_count}</b> incidents · {r.event_count} total events</span>
              </li>
            ))}
          </ul>
        )}
        <Link to="/incidents" className="mt-4 inline-block text-xs text-primary">View full incidents list →</Link>
      </div>
    </div>
  );
}

function Stat({ label, value, className }: { label: string; value: number; className?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className={`text-2xl font-bold ${className ?? ""}`}>{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}
